import { logger } from '@heidi/runtime/logger';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { GET_SPECIALTIES } from '../../contracts/keys';
import { getSpecialties, type SpecialtyListItem } from '../api/specialties';
import { SpecialtyCountryUnsupportedError } from './specialty-country-unsupported-error';

export { GET_SPECIALTIES };

export type RawSpecialtiesResponse = {
  specialties: SpecialtyListItem[];
  total: number;
};

export function useQuerySpecialtiesCore<T = RawSpecialtiesResponse>(options: {
  country?: string;
  enabled?: boolean;
  locale: string;
  select?: (data: RawSpecialtiesResponse) => T;
}) {
  const country = options.country;

  return useQuery<RawSpecialtiesResponse, Error, T>({
    queryKey: [
      GET_SPECIALTIES,
      { ...(country ? { country } : {}), locale: options.locale },
    ],
    enabled: options.enabled,
    select: options.select,
    placeholderData: (previousData, previousQuery) => {
      const previousCountry = (
        previousQuery?.queryKey[1] as { country?: string } | undefined
      )?.country;
      return previousCountry === country ? previousData : undefined;
    },
    retry: (failureCount, error) =>
      !(error instanceof SpecialtyCountryUnsupportedError) && failureCount < 1,
    queryFn: async (): Promise<RawSpecialtiesResponse> => {
      try {
        const { data } = await getSpecialties(country, options.locale);
        return {
          specialties: data.specialties,
          total: data.total,
        };
      } catch (error) {
        if (country && error instanceof AxiosError && error.response?.status === 422) {
          logger.error(
            `[specialties] backend rejected country "${country}" (422) — FE country list is ahead of the backend CountryCode enum`
          );
          throw new SpecialtyCountryUnsupportedError(country);
        }
        throw new Error('Failed to fetch specialties');
      }
    },
  });
}
